<script lang="ts">
import { Options as PlyrOptions } from 'plyr';
import { computed, onBeforeUnmount, onMounted, ref, useId, type Ref } from 'vue';

import './plyr.css';

import PhxsgVideoPlayerIframe from './phxsg-video-player-iframe.vue';
import PhxsgVideoPlayerVideo, { type VideoSource, type VideoCaption } from './phxsg-video-player-video.vue';
import { useEmbeddableMedia } from './use-embeddable-media';
import { useVideoOptions } from './use-video-options';
import { VideoPlayer, type VideoTrackingOptions } from './video-player';

export type { VideoSource, VideoCaption, PlyrOptions };

export type VideoFit = 'cover' | 'contain' | 'none';

export interface PhxsgVideoPlayerProps {
  id?: string | null;
  initialize?: boolean;
  poster?: string | null;
  playButtonOverlay?: boolean;
  fit?: VideoFit | null;
  rootClass?: string | null;
  embed?: string | null;
  plyr?: boolean;
  plyrOptions?: PlyrOptions;
  prefetch?: boolean;
  hasPlaylistCapabilities?: boolean;
  videoTracking?: VideoTrackingOptions;
  controlsHtml?: string | null;
  sources?: VideoSource[];
  captions?: VideoCaption[];
  lazy?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsinline?: boolean;
  controls?: boolean;
}

interface PlayerMountPayload {
  player: Ref<VideoPlayer | null>;
  playerElement: Ref<HTMLElement | null>;
  VideoPlayer: typeof VideoPlayer;
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  id = null,
  initialize = true,
  poster = null,
  playButtonOverlay = true,
  fit = null,
  rootClass = null,
  embed = null,
  plyr = true,
  plyrOptions = {},
  prefetch = false,
  hasPlaylistCapabilities = false,
  videoTracking = {},
  controlsHtml = null,
  sources = [],
  captions = [],
  lazy = true,
  autoplay = false,
  muted = false,
  loop = false,
  playsinline = true,
  controls = true,
} = defineProps<PhxsgVideoPlayerProps>();

const emit = defineEmits<{
  'video-player:mounted': [PlayerMountPayload];
  play: [Event];
  pause: [Event];
  timeupdate: [Event];
  controlshidden: [Event];
  controlsshown: [Event];
  enterfullscreen: [Event];
  exitfullscreen: [Event];
  'player-ready': [CustomEvent];
}>();

const { isEmbed, isYoutube } = useEmbeddableMedia(() => embed);

const videoOptions = useVideoOptions(() => ({
  plyr,
  plyrOptions,
  prefetch,
  hasPlaylistCapabilities,
  videoTracking,
  controlsHtml,
}));

const player: Ref<VideoPlayer | null> = ref(null);
const playerElement: Ref<HTMLElement | null> = ref(null);

const generatedId = useId();
const computedId = computed(() => id ?? `video-player-${generatedId}`);

onMounted(() => {
  if (!initialize) {
    emit('video-player:mounted', {
      player,
      playerElement,
      VideoPlayer,
    });
    return;
  }

  if (playerElement.value) {
    player.value = new VideoPlayer(playerElement.value, videoOptions.value);
  }
});

onBeforeUnmount(() => {
  player.value?.destroy?.();
});

defineExpose({ playerElement, player });
</script>

<template>
  <div
    :id="computedId"
    ref="playerElement"
    :class="[
      'phxsg-video-player',
      {
        'phxsg-video-player--youtube': isYoutube,
        'phxsg-video-player--embed': isEmbed,
        [`phxsg-video-player--${fit}`]: fit && fit !== 'none',
      },
      rootClass,
    ]"
  >
    <template v-if="isEmbed">
      <div :id="`${computedId}-embed`" class="plyr__video-embed">
        <PhxsgVideoPlayerIframe :src="embed!" :poster="poster" v-bind="$attrs" />
      </div>
    </template>

    <PhxsgVideoPlayerVideo
      v-else
      :sources="sources"
      :captions="captions"
      :poster="poster"
      :lazy="lazy"
      :autoplay="autoplay"
      :muted="muted || autoplay"
      :loop="loop"
      :playsinline="playsinline"
      :controls="controls && !plyr"
      class="phxsg-video-player__video"
      v-bind="$attrs"
      @play="emit('play', $event)"
      @pause="emit('pause', $event)"
      @timeupdate="emit('timeupdate', $event)"
      @controlshidden="emit('controlshidden', $event)"
      @controlsshown="emit('controlsshown', $event)"
      @enterfullscreen="emit('enterfullscreen', $event)"
      @exitfullscreen="emit('exitfullscreen', $event)"
      @player-ready="emit('player-ready', $event)"
    />

    <template v-if="$slots['play-button'] && playButtonOverlay">
      <slot name="play-button" />
    </template>

    <slot />
  </div>
</template>

<style src="./video-player.css" />
